import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatearMoneda } from "../../config/moneda";
import { useStorePos } from "./useStorePos";

const PosComprobacionFiscal = () => {
  const { t } = useTranslation();
  const visible = useStorePos((state) => state.ComprobacionFiscalVisible);
  const cargando = useStorePos((state) => state.CargandoComprobacionFiscal);
  const comprobacion = useStorePos((state) => state.ComprobacionFiscal);
  const resultado = useStorePos((state) => state.ResultadoVenta);
  const condicion = resultado?.condicionIVA || resultado?.opcionFacturacion?.CondicionIVA || t("pos.notReported");

  const cerrar = () => useStorePos.setState({ ComprobacionFiscalVisible: false });

  useEffect(() => {
    if (!visible) return undefined;
    if (!comprobacion && !cargando) useStorePos.getState().cargarComprobacionFiscal();

    const cerrarConEscape = (event) => {
      if (event.key === "Escape") useStorePos.setState({ ComprobacionFiscalVisible: false });
    };
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", cerrarConEscape);

    return () => {
      document.body.style.overflow = overflowAnterior;
      document.removeEventListener("keydown", cerrarConEscape);
    };
  }, [visible, comprobacion, cargando]);

  if (!visible) return null;
  const factura = comprobacion?.Factura;
  const estado = factura?.Estado || t("pos.noStatus");
  const estadoNormalizado = estado.toLowerCase();
  const estadoClase = estadoNormalizado.includes("autoriz")
    ? "exitoso"
    : estadoNormalizado.includes("error") || estadoNormalizado.includes("rechaz")
      ? "error"
      : "pendiente";
  const alicuotas = comprobacion?.AlicuotasIVA || [];
  const detalle = comprobacion?.Detalle || [];

  return (
    <div className="pos-modal-backdrop pos-comprobacion-backdrop" role="presentation" onMouseDown={cerrar}>
      <section
        className="pos-modal pos-comprobacion"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-comprobacion-titulo"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="pos-comprobacion-header">
          <div className="pos-comprobacion-heading">
            <span className="pos-comprobacion-icon" aria-hidden="true"><i className="pi pi-verified" /></span>
            <div>
              <span className="pos-step">{t("pos.persistedData")}</span>
              <h3 id="pos-comprobacion-titulo">{t("pos.fiscalCheck")}</h3>
              <p>{t("pos.fiscalCheckHelp")}</p>
            </div>
          </div>
          <button type="button" className="pos-icon-btn pos-comprobacion-cerrar" onClick={cerrar} aria-label={t("pos.closeFiscalCheck")}><i className="pi pi-times" /></button>
        </div>
        {cargando && (
          <div className="pos-comprobacion-cargando" role="status">
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            <span>{t("pos.loadingFiscalDetails")}</span>
          </div>
        )}
        {comprobacion?.Error && <p className="pos-resultado-error" role="alert">{comprobacion.Error}</p>}
        {factura && (
          <>
            <div className="pos-comprobacion-resumen">
              <div className="pos-comprobacion-identificacion">
                <div>
                  <span>{t("pos.receipt")}</span>
                  <strong>{factura.Comprobante || factura.FacturaId}</strong>
                </div>
                <div>
                  <span>{t("pos.vatConditionLong")}</span>
                  <strong>{condicion}</strong>
                </div>
              </div>
              <div className="pos-comprobacion-estado">
                <span>{t("pos.fiscalStatus")}</span>
                <strong className={`pos-comprobacion-estado-chip ${estadoClase}`}>
                  <i className={estadoClase === "exitoso" ? "pi pi-check-circle" : "pi pi-info-circle"} aria-hidden="true" />
                  {estado}
                </strong>
              </div>
              <div className="pos-comprobacion-importes">
                <div><span>{t("pos.net")}</span><strong>{formatearMoneda(factura.Neto)}</strong></div>
                <div><span>{t("pos.tax")}</span><strong>{formatearMoneda(factura.Iva)}</strong></div>
                <div><span>{t("pos.otherTaxes")}</span><strong>{formatearMoneda(factura.Tributos)}</strong></div>
                <div className="total"><span>{t("pos.receiptTotal")}</span><strong>{formatearMoneda(factura.Total)}</strong></div>
              </div>
            </div>

            <section className="pos-comprobacion-seccion" aria-labelledby="pos-alicuotas-titulo">
              <div className="pos-comprobacion-seccion-header">
                <div>
                  <h4 id="pos-alicuotas-titulo">{t("pos.detailTaxRates")}</h4>
                  <p>{t("pos.taxComposition")}</p>
                </div>
                <span>{t("pos.taxRateCount", { count: alicuotas.length })}</span>
              </div>
              <div className="pos-alicuotas">
                {alicuotas.map((item) => (
                  <article key={item.Porcentaje}>
                    <div className="pos-alicuota-tasa"><span>{t("pos.tax")}</span><strong>{item.Porcentaje}%</strong></div>
                    <dl>
                      <div><dt>{t("pos.taxedNet")}</dt><dd>{formatearMoneda(item.Neto)}</dd></div>
                      <div><dt>{t("pos.tax")}</dt><dd>{formatearMoneda(item.Iva)}</dd></div>
                      <div className="total"><dt>{t("common.total")}</dt><dd>{formatearMoneda(item.Total)}</dd></div>
                    </dl>
                  </article>
                ))}
              </div>
            </section>

            <section className="pos-comprobacion-seccion" aria-labelledby="pos-detalle-fiscal-titulo">
              <div className="pos-comprobacion-seccion-header">
                <div>
                  <h4 id="pos-detalle-fiscal-titulo">{t("pos.productDetail")}</h4>
                  <p>{t("pos.persistedLineAmounts")}</p>
                </div>
                <span>{t("pos.lineCount", { count: detalle.length })}</span>
              </div>
              <div className="pos-ticket-table-wrap pos-comprobacion-tabla-wrap">
                <table className="pos-ticket-table pos-comprobacion-tabla">
                  <thead><tr><th>#</th><th>{t("pos.product")}</th><th className="num">{t("pos.taxRate")}</th><th className="num">{t("pos.net")}</th><th className="num">{t("pos.tax")}</th><th className="num">{t("common.total")}</th></tr></thead>
                  <tbody>{detalle.map((linea) => (
                    <tr key={linea.Linea}>
                      <td data-label={t("pos.line")}>{linea.Linea}</td>
                      <td data-label={t("pos.product")}>{`${linea.Codigo || ""} ${linea.Descripcion || ""}`.trim() || t("pos.withoutDescription")}</td>
                      <td data-label={t("pos.taxRate")} className="num">{Number(linea.PorcentajeIva) || 0}%</td>
                      <td data-label={t("pos.net")} className="num">{formatearMoneda(linea.Neto)}</td>
                      <td data-label={t("pos.tax")} className="num">{formatearMoneda(linea.Iva)}</td>
                      <td data-label={t("common.total")} className="num pos-comprobacion-linea-total">{formatearMoneda(linea.Total)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>

            <div className={`pos-comprobacion-conciliacion ${comprobacion.DiferenciaSignificativa ? "error" : "ok"}`} role="status">
              <i className={comprobacion.DiferenciaSignificativa ? "pi pi-exclamation-triangle" : "pi pi-check-circle"} aria-hidden="true" />
              <div>
                <strong>{comprobacion.DiferenciaSignificativa ? t("pos.differenceDetected") : t("pos.totalsReconciled")}</strong>
                <span>{t("pos.reconciliationFormula")}</span>
              </div>
              <strong className="pos-comprobacion-diferencia">{formatearMoneda(comprobacion.Diferencia)}</strong>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PosComprobacionFiscal;
